import { useEffect, useState } from 'react';

/**
 * Filtros do kanban de ordens de serviço (busca + período de emissão),
 * persistidos em `localStorage` para sobreviver a fechar e abrir o
 * programa. Vale para os dois alvos: no desktop o Electron guarda no perfil
 * do app; no web, no navegador.
 *
 * Atalho de período ("Hoje", "7 dias", "Este mês") é salvo pelo nome, não
 * pelas datas: quem deixou "Este mês" marcado em setembro abre em outubro e
 * vê outubro. Datas digitadas à mão ficam fixas.
 */

export const CHAVE_FILTROS_KANBAN = 'orcamento.kanban.filtros';

export type PresetPeriodo = 'hoje' | '7d' | 'mes';

const PRESETS: PresetPeriodo[] = ['hoje', '7d', 'mes'];

type FiltrosKanban = {
  busca: string;
  dataInicio: string;
  dataFim: string;
  /** Atalho que gerou o período atual; null = datas digitadas (ou vazias). */
  preset: PresetPeriodo | null;
};

const VAZIO: FiltrosKanban = { busca: '', dataInicio: '', dataFim: '', preset: null };

/** Data local no formato YYYY-MM-DD, aceito pelo <input type="date">. */
function isoData(d: Date): string {
  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

/** Intervalo [início, fim] (YYYY-MM-DD) de cada atalho de período. */
export function intervaloPreset(modo: PresetPeriodo): [string, string] {
  const fim = new Date();
  const inicio = new Date();
  if (modo === '7d') inicio.setDate(inicio.getDate() - 6); // hoje + 6 dias atrás = 7 dias
  else if (modo === 'mes') inicio.setDate(1);
  return [isoData(inicio), isoData(fim)];
}

function dataValida(v: unknown): v is string {
  return typeof v === 'string' && (v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v));
}

function ler(): FiltrosKanban {
  try {
    const bruto = localStorage.getItem(CHAVE_FILTROS_KANBAN);
    if (!bruto) return VAZIO;
    const salvo = JSON.parse(bruto) as Partial<FiltrosKanban> | null;
    if (!salvo || typeof salvo !== 'object') return VAZIO;
    const busca = typeof salvo.busca === 'string' ? salvo.busca : '';
    if (salvo.preset && PRESETS.includes(salvo.preset)) {
      // Recalcula a partir de hoje — o atalho é relativo à data de abertura.
      const [dataInicio, dataFim] = intervaloPreset(salvo.preset);
      return { busca, dataInicio, dataFim, preset: salvo.preset };
    }
    return {
      busca,
      dataInicio: dataValida(salvo.dataInicio) ? salvo.dataInicio : '',
      dataFim: dataValida(salvo.dataFim) ? salvo.dataFim : '',
      preset: null,
    };
  } catch {
    return VAZIO;
  }
}

function gravar(filtros: FiltrosKanban): void {
  try {
    localStorage.setItem(CHAVE_FILTROS_KANBAN, JSON.stringify(filtros));
  } catch {
    // localStorage indisponível (modo privado/sandbox) — segue só em memória.
  }
}

export function useFiltrosKanban() {
  const [filtros, setFiltros] = useState<FiltrosKanban>(ler);

  useEffect(() => {
    gravar(filtros);
  }, [filtros]);

  const { busca, dataInicio, dataFim } = filtros;

  return {
    busca,
    dataInicio,
    dataFim,
    setBusca: (busca: string) => setFiltros((f) => ({ ...f, busca })),
    // Mexeu na data à mão: o período deixa de ser um atalho e fica fixo.
    setDataInicio: (dataInicio: string) =>
      setFiltros((f) => ({ ...f, dataInicio, preset: null })),
    setDataFim: (dataFim: string) => setFiltros((f) => ({ ...f, dataFim, preset: null })),
    aplicarPreset(modo: PresetPeriodo) {
      const [inicio, fim] = intervaloPreset(modo);
      setFiltros((f) => ({ ...f, dataInicio: inicio, dataFim: fim, preset: modo }));
    },
    /** Atalho está ativo quando o intervalo atual bate exatamente com o dele. */
    presetAtivo(modo: PresetPeriodo): boolean {
      const [inicio, fim] = intervaloPreset(modo);
      return dataInicio === inicio && dataFim === fim;
    },
    limparPeriodo() {
      setFiltros((f) => ({ ...f, dataInicio: '', dataFim: '', preset: null }));
    },
  };
}
